import { supabase } from "@/integrations/supabase/client";
import type { AdditionalQuestionRequest, AdditionalQuestionResponse } from "@/types/additional-questions";
import type { Question } from "@/types/test";

export const generateAdditionalQuestions = async (
  request: AdditionalQuestionRequest
): Promise<AdditionalQuestionResponse> => {
  try {
    console.log('Calling generate-additional-questions function with:', {
      skillCount: request.skills.length,
      skills: request.skills,
      distribution: request.distribution,
    });

    const { data, error } = await supabase.functions.invoke('generate-additional-questions', {
      body: {
        skills: request.skills,
        distribution: request.distribution,
        difficulty: request.difficulty,
        additionalComments: request.additionalComments,
        testContext: request.testContext,
      },
    });

    if (error) {
      console.error('Supabase function error:', error);
      throw error;
    }

    if (!data) {
      throw new Error('No data returned from server');
    }

    if (!data.success) {
      throw new Error(data.error || 'Question generation failed');
    }

    if (!data.questions || !Array.isArray(data.questions)) {
      throw new Error('Invalid response format from server');
    }

    console.log(`Successfully generated ${data.questions.length} questions`);

    return {
      success: true,
      questions: data.questions as Question[],
    };
  } catch (error) {
    console.error('Additional questions generation failed:', error);
    return {
      success: false,
      questions: [],
      error: error instanceof Error ? error.message : 'Unknown error occurred',
    };
  }
};

export const validateQuestion = (question: Question): boolean => {
  const requiredFields = ['id', 'question_type', 'content', 'order'];
  const hasRequiredFields = requiredFields.every(field => question[field as keyof Question] !== undefined);
  
  if (!hasRequiredFields) {
    console.warn('Question missing required fields:', question);
    return false;
  }
  
  if (question.question_type === 'multiple_choice') {
    const content = question.content as any;
    const isValid = content.options?.length === 4 && 
           typeof content.correctOption === 'number' &&
           content.correctOption >= 0 && content.correctOption <= 3;
    
    if (!isValid) {
      console.warn('Invalid multiple choice question:', question);
    }
    return isValid;
  }
  
  return true;
};
